import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleAutoMessages: React.FC = () => {
  const BASE = '/images/help/auto-messages';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/am-step-1.png`, alt: 'Automated messages overview', caption: 'Step 1 – How automated buyer messages keep your customers informed' },
    ],
    step2: [
      { src: `${BASE}/am-step-2.png`, alt: 'Message types and triggers', caption: 'Step 2 – When each message type is triggered' },
    ],
    step3: [
      { src: `${BASE}/am-step-3.png`, alt: 'Enable automated messages settings', caption: 'Step 3 – Enable automated messages in your settings' },
    ],
    step4: [
      { src: `${BASE}/am-step-4.png`, alt: 'Message template customisation', caption: 'Step 4 – Customise your message templates with dynamic placeholders' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Automated Buyer Messages | CodeRom Help"
        description="Set up automatic eBay buyer messages for dispatch, delivery & custom notifications."
      />
      <HelpBreadcrumbSchema slug="auto-messages" name="Automated buyer messages" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Automated buyer messages</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>Automated buyer messages</h1>
                <p className="article-subtitle">
                  Automatically send professional messages to your eBay buyers at key moments — order confirmation, dispatch notification, and delivery follow-up. Build trust and encourage positive feedback.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 5 min read</span>
                  <span className="category">Messaging</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. What are automated messages?</a></li>
                  <li><a href="#step2">2. Message types and triggers</a></li>
                  <li><a href="#step3">3. Enable auto messages</a></li>
                  <li><a href="#step4">4. Customise message templates</a></li>
                  <li><a href="#step5">5. Message settings</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: What are automated messages?</h2>
                  <p>
                    Automated messages are sent to your eBay buyers at important moments during the order process. They help you:
                  </p>
                  <ul>
                    <li>Confirm that the order is being processed</li>
                    <li>Notify the buyer when their item has been dispatched</li>
                    <li>Follow up after delivery to encourage positive feedback</li>
                    <li>Maintain professional communication without manual effort</li>
                  </ul>
                  <p>
                    All messages are sent through eBay's messaging system — buyers receive them in their eBay inbox.
                  </p>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: What are automated messages?" />
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: Message types and triggers</h2>

                  <div className="message-types-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Message type</th>
                          <th>When it's sent</th>
                          <th>Purpose</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Order confirmation</strong></td>
                          <td>When a new order is received</td>
                          <td>Reassure the buyer their order is being processed</td>
                        </tr>
                        <tr>
                          <td><strong>Dispatch notification</strong></td>
                          <td>When tracking is uploaded</td>
                          <td>Let the buyer know their item is on the way</td>
                        </tr>
                        <tr>
                          <td><strong>Delivery follow-up</strong></td>
                          <td>A few days after delivery</td>
                          <td>Thank the buyer and request feedback</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: Message types and triggers" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> The delivery follow-up message is your best opportunity to get positive feedback. A friendly, professional message at the right time can significantly improve your feedback score.
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Enable auto messages</h2>
                  <ol>
                    <li>Go to <strong>Settings → Auto Messages</strong></li>
                    <li>Toggle <strong>Auto Messages</strong> to On</li>
                    <li>Choose which message types you want to enable:
                      <ul>
                        <li>Order confirmation ✓</li>
                        <li>Dispatch notification ✓</li>
                        <li>Delivery follow-up ✓</li>
                      </ul>
                    </li>
                    <li>Click <strong>Save</strong></li>
                  </ol>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Enable auto messages" />
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Customise message templates</h2>
                  <p>
                    Each message type has a default template that you can customise:
                  </p>
                  <ol>
                    <li>Go to <strong>Settings → Auto Messages</strong></li>
                    <li>Click <strong>Edit Template</strong> next to any message type</li>
                    <li>Modify the message text</li>
                    <li>Use placeholders for dynamic content:</li>
                  </ol>

                  <div className="placeholders-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Placeholder</th>
                          <th>Replaced with</th>
                          <th>Example</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><code>{'{buyer_name}'}</code></td>
                          <td>Buyer's eBay username</td>
                          <td>john_smith_123</td>
                        </tr>
                        <tr>
                          <td><code>{'{item_title}'}</code></td>
                          <td>Product title</td>
                          <td>Fire TV Stick 4K Max</td>
                        </tr>
                        <tr>
                          <td><code>{'{order_number}'}</code></td>
                          <td>eBay order number</td>
                          <td>12-34567-89012</td>
                        </tr>
                        <tr>
                          <td><code>{'{tracking_number}'}</code></td>
                          <td>Shipment tracking number</td>
                          <td>AB123456789GB</td>
                        </tr>
                        <tr>
                          <td><code>{'{delivery_estimate}'}</code></td>
                          <td>Estimated delivery date</td>
                          <td>15 April 2026</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step4} stepLabel="Step 4: Customise message templates" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Keep your messages short, friendly, and professional. Avoid asking for positive feedback directly — eBay's policies prohibit this. Instead, say something like "We hope you enjoy your purchase. If you have any concerns, please let us know."
                    </div>
                  </div>
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Message settings</h2>

                  <div className="settings-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Auto messages enabled</strong></td>
                          <td>Master toggle for all automated messages</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Order confirmation</strong></td>
                          <td>Send when order is received</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Dispatch notification</strong></td>
                          <td>Send when tracking is uploaded</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Delivery follow-up</strong></td>
                          <td>Send after estimated delivery</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Follow-up delay</strong></td>
                          <td>Days to wait after delivery before follow-up</td>
                          <td>2-3 days</td>
                        </tr>
                        <tr>
                          <td><strong>Send to repeat buyers</strong></td>
                          <td>Send messages to returning customers</td>
                          <td>On</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that auto messages are set up, explore these features:</p>
                  <div className="next-steps">
                    <Link to="/help/gmail-tracking" className="next-step-card">
                      <div className="next-step-icon">📧</div>
                      <div className="next-step-content">
                        <h4>Gmail integration & tracking</h4>
                        <p>Automatically capture Amazon tracking numbers from your Gmail inbox</p>
                      </div>
                    </Link>
                    <Link to="/help/orders" className="next-step-card">
                      <div className="next-step-icon">📋</div>
                      <div className="next-step-content">
                        <h4>Order management & fulfilment</h4>
                        <p>Manage your eBay orders from one place</p>
                      </div>
                    </Link>
                    <Link to="/help/reports" className="next-step-card">
                      <div className="next-step-icon">📊</div>
                      <div className="next-step-content">
                        <h4>Daily reports & notifications</h4>
                        <p>Get daily summaries of sales, profit, and inventory updates</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section ul,
        .content-section ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .content-section li {
          margin-bottom: 8px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }
        .content-section code {
          background: #f1f5f9;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          font-size: 14px;
          color: #6366f1;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Tables */
        .message-types-table,
        .placeholders-table,
        .settings-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .message-types-table table,
        .placeholders-table table,
        .settings-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .message-types-table th,
        .placeholders-table th,
        .settings-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .message-types-table td,
        .placeholders-table td,
        .settings-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .message-types-table tbody tr:last-child td,
        .placeholders-table tbody tr:last-child td,
        .settings-table tbody tr:last-child td {
          border-bottom: none;
        }
        .message-types-table tbody tr:hover,
        .placeholders-table tbody tr:hover,
        .settings-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .message-types-table,
          .placeholders-table,
          .settings-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticleAutoMessages;
